import React, { useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import jwtDecode from "jwt-decode";

import { CreatorRole } from "../../models";
import { getCountryFromUrl } from "../../i18n";
import { segmentService } from "../../legacy/segment/SegmentService";
import { useAuthenticationData } from "../../next/hooks";
import { useCreateOperation } from "../../next/hooks";

import Form from "./Form";
import ResponsiveShell from "./ResponsiveShell";
import FormattedMessage from "./FormattedMessage";
import { InjectedTranslations } from "../../models/locale";

type CreateOperationFields = {
  newOperationName: string;
  creatorRole: CreatorRole;
};

export const InjectedTranslationContext = React.createContext<
  InjectedTranslations | undefined
>(undefined);

const userIsAdvisor = (idToken: string): boolean => {
  try {
    const decodedKey: Record<string, unknown> = jwtDecode(idToken);

    if (
      "cognito:groups" in decodedKey &&
      (decodedKey["cognito:groups"] as string[]).includes("certified_advisor")
    ) {
      return true;
    }
  } catch {
    console.warn("Could not decode ID Token to check if user is a CSA!");
    return false;
  }

  return false;
};

const CreateOperationSchema = Yup.object().shape({
  newOperationName: Yup.string().required(() => {
    return <FormattedMessage id="must_not_be_empty" />;
  }),
  creatorRole: Yup.string().required(),
});

const initialValues: CreateOperationFields = {
  newOperationName: "",
  creatorRole: "administrator",
};

const Initial: React.FC<{
  UNSAFE_injectedTranslations?: InjectedTranslations;
}> = ({ UNSAFE_injectedTranslations }) => {
  useEffect(() => {
    segmentService.track("add operation");
  }, []);

  const authData = useAuthenticationData();
  const { mutate: createOperation } = useCreateOperation();
  const region = getCountryFromUrl();

  /**
   * We only show the role select dropdown if the user is an advisor AND the
   * region is NOT Brazil (because they do not have advisors there for some
   * reason 🤷‍♀️)
   *
   * The Good Thing™ about that last conditional is that you'll see the
   * extended options if you're developing locally :)
   */
  const isAdvisor = userIsAdvisor(authData.identityToken);
  const showRoleDropdown = isAdvisor && region !== "br";

  /**
   * TODO: What if this fails? Where's the alert? Use the 'status' and
   * 'activity' fields from the useOperations hook to keep an alert handy!
   */
  const handleSubmit = async (values: CreateOperationFields) => {
    segmentService.track("add operation started");
    createOperation({
      operationName: values.newOperationName.trim(),
      creatorRole: values.creatorRole,
    });
  };

  return (
    <ResponsiveShell>
      <InjectedTranslationContext.Provider value={UNSAFE_injectedTranslations}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={CreateOperationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={(values) => handleSubmit(values)}
        >
          {(props) => <Form showRoleDropdown={showRoleDropdown} {...props} />}
        </Formik>
      </InjectedTranslationContext.Provider>
    </ResponsiveShell>
  );
};

export default Initial;
